import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCrearPlato from "./ModalCrearPlato";
import MenuProductImage from "../menu/MenuProductImage";
import MenuImportPanel from "../menu/MenuImportPanel";
import { getInternalAssistantEnvironment } from "../../services/local/internalAssistantRuntime";
import { optimizeImageFile } from "../../services/images/optimizeImage";
import { useAccessControl } from "../context/useAccessControl";
import { useIngredientes } from "../context/IngredientesContext";
import {
  loadLocalMenuSnapshot,
  saveLocalMenuSnapshot,
  toLightMenuSnapshot,
} from "../../services/local/menuStorage";
import {
  getIngredientStock,
  getOperationalProductStockState,
  hydrateIngredientStockFromRemote,
  INGREDIENT_STOCK_UPDATED_EVENT,
} from "../../services/local/ingredientStock";
import {
  createMenuCategory,
  createMenuSubcategory,
  deleteMenuSubcategoryLink,
  deleteMenuCategory,
  deleteMenuProduct,
  deleteMenuSubcategory,
  getBranchMenuSnapshot,
  saveMenuSubcategoryLink,
  saveMenuProduct,
  updateMenuProductPresentation,
  saveMenuProductsDeliveryAvailability,
} from "../../services/supabase/menuService";
import { isSupabaseConfigured } from "../../services/supabase/config";
import { buildCategoryIcon } from "../../services/menu/defaultMenuConfig";
import { buildLinkedRuleKey, isIngredientLinkedRule } from "../../services/menu/linkedRules";
import { isBeverageCategory } from "../../services/menu/beverageRelations";
import { isProductVisibleInMenu } from "../../services/menu/productVisibility";
import useLinkedProductChoices from "../menu/useLinkedProductChoices";
import "../../styles/MenuAdmin.css";
import {
  readSessionUiState,
  removeSessionUiState,
  writeSessionUiState,
} from "../../services/local/uiSessionState";

const createLocalId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const readFileAsDataUrl = (file) => optimizeImageFile(file, { profile: "product" });

const CategoriaBoton = ({ icon, label, total, active, preview, onClick, imagesEnabled }) => (
  <button
    type="button"
    className="menu-admin__category"
    style={{
      ...styles.btnCategoria,
      ...(active ? styles.btnCategoriaActiva : {}),
    }}
    onClick={onClick}
    title={label}
  >
    {preview?.length > 0 ? (
      <div style={styles.previewStrip}>
        {preview.map((plato) => (
          <MenuProductImage key={plato.id} product={plato} enabled={imagesEnabled}
            style={styles.previewThumb} fallback={icon} />
        ))}
      </div>
    ) : (
      <div style={styles.emoji}>{icon}</div>
    )}
    <div style={styles.categoriaNombre}>{label}</div>
    <div style={styles.categoriaMeta}>{total} platos</div>
  </button>
);

const contarIngredientesPlato = (ingredientes = []) =>
  ingredientes.reduce((total, ingrediente) => {
    if (typeof ingrediente === "object" && ingrediente !== null) {
      return total + Number(ingrediente.cantidad || 0);
    }

    return total + 1;
  }, 0);

const normalizeEntityId = (value) => String(value || "").trim();

const hasOwnEntry = (record, key) =>
  Object.prototype.hasOwnProperty.call(record, key);

const buildLinkedRuleTargetLabel = (rule) => {
  if (isIngredientLinkedRule(rule)) return `${rule.targetIngredientName || "Ingrediente"} / ${rule.consumptionMl} ml por vaso`;
  const targetCategoryName =
    rule?.targetCategoryName || rule?.target_category_name || "Categoria";
  const targetSubcategoryName =
    rule?.targetSubcategoryName || rule?.target_subcategory_name || "";

  const groupLabel = targetSubcategoryName
    ? `${targetCategoryName} / ${targetSubcategoryName}`
    : `${targetCategoryName} / Todas las subcategorias`;
  return rule.targetProductName ? `${groupLabel} / ${rule.targetProductName}` : groupLabel;
};

const mergeLinkedRules = (remoteRules = [], localRules = []) => {
  const merged = new Map();

  [...remoteRules, ...localRules].forEach((rule) => {
    const sourceKey = normalizeEntityId(
      rule?.sourceSubcategoryId || rule?.source_subcategory_id
    );
    const targetCategoryKey = normalizeEntityId(
      rule?.targetCategoryId || rule?.target_category_id
    );
    const targetSubcategoryKey = normalizeEntityId(
      rule?.targetSubcategoryId || rule?.target_subcategory_id
    );

    if ((!sourceKey && !rule.sourceCategoryId) || (!targetCategoryKey && !targetSubcategoryKey && !isIngredientLinkedRule(rule))) {
      return;
    }

    const normalizedRule = {
      ...rule,
      sourceSubcategoryId: sourceKey,
      targetCategoryId: targetCategoryKey,
      targetSubcategoryId: targetSubcategoryKey,
      sourceSubcategoryName:
        rule?.sourceSubcategoryName || rule?.source_subcategory_name || "",
      targetCategoryName:
        rule?.targetCategoryName || rule?.target_category_name || "",
      targetSubcategoryName:
        rule?.targetSubcategoryName || rule?.target_subcategory_name || "",
      selectionMode: "single_optional",
      title: rule?.title || "",
      active: rule?.active !== false,
    };

    merged.set(buildLinkedRuleKey(normalizedRule), normalizedRule);
  });

  return Array.from(merged.values()).sort((left, right) =>
    buildLinkedRuleTargetLabel(left).localeCompare(buildLinkedRuleTargetLabel(right), "es")
  );
};

const isMissingLinkedRulesTableError = (error) =>
  String(error?.message || "")
    .toLowerCase()
    .includes("menu_subcategory_links") ||
  String(error?.message || "")
    .toLowerCase()
    .includes("target_category_id");

const buildMenuViewStateKey = (branchId) =>
  `mozzoapp:menu-view:${branchId || "default"}`;

const buildMenuDraftKey = (branchId) =>
  `mozzoapp:menu-draft:${branchId || "default"}`;

function Menu() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    canAccessModule,
    canPerformAction,
    currentAccessRoleLabel,
    currentBranch,
    currentRestaurant,
    isCreatorMode,
    session,
  } = useAccessControl();
  const { ingredientes } = useIngredientes();
  const [categorias, setCategorias] = useState([]);
  const [subcategoriasPorCategoria, setSubcategoriasPorCategoria] = useState({});
  const [linkedSubcategoryRules, setLinkedSubcategoryRules] = useState([]);
  const [platos, setPlatos] = useState([]);
  const [ingredientStockMap, setIngredientStockMap] = useState({});
  const [menuId, setMenuId] = useState("");
  const [menuMode, setMenuMode] = useState("shared");
  const [storageMode, setStorageMode] = useState(
    isSupabaseConfigured && !isCreatorMode ? "supabase" : "local"
  );
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [menuVerified, setMenuVerified] = useState(false);
  const menuRequestRef = useRef(0);
  const imageScope = session?.user?.id || "";
  const [savingMenu, setSavingMenu] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [pendingDeliveryAvailability, setPendingDeliveryAvailability] = useState({});
  const [modalCrearPlato, setModalCrearPlato] = useState(false);
  const [categoriaActivaId, setCategoriaActivaId] = useState(null);
  const [linkedRulesExpanded, setLinkedRulesExpanded] = useState(false);
  const linkedRulesPanelId = useId();
  const [sourceSubcategoryId, setSourceSubcategoryId] = useState("");
  const [sourceProductId, setSourceProductId] = useState("");
  const [targetCategoryId, setTargetCategoryId] = useState("");
  const [targetSubcategoryId, setTargetSubcategoryId] = useState("");
  const [targetProductId, setTargetProductId] = useState("");
  const [linkTargetType, setLinkTargetType] = useState("product");
  const [targetIngredientId, setTargetIngredientId] = useState("");
  const [consumptionMl, setConsumptionMl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [categoriaInicialModal, setCategoriaInicialModal] = useState("");
  const [platoEnEdicion, setPlatoEnEdicion] = useState(null);
  const canSeeMenuModule = canAccessModule("menu");
  const menuReadOnly = storageMode === "supabase" && (loadingMenu || !menuVerified);
  const canManageMenu = canSeeMenuModule && canPerformAction("manage_menu") && !menuReadOnly;
  const canManageDeliveryAvailability =
    canSeeMenuModule && canPerformAction("manage_sales_channels") && !menuReadOnly;
  const currentBranchStockEnabled = currentBranch?.stockEnabled;
  const currentRestaurantStockEnabled = currentRestaurant?.stockEnabled;
  const activeStockScope = useMemo(
    () => ({
      id: currentBranch?.id || currentRestaurant?.id || "",
      stockEnabled:
        (currentBranch?.id
          ? currentBranchStockEnabled
          : currentRestaurantStockEnabled) !== false,
    }),
    [
      currentBranch?.id,
      currentBranchStockEnabled,
      currentRestaurant?.id,
      currentRestaurantStockEnabled,
    ]
  );
  const activeStockScopeId = activeStockScope?.id || "";
  const isStockFeatureEnabled = activeStockScope?.stockEnabled !== false;
  const menuViewStateHydratedRef = useRef("");
  const menuViewStateKey = useMemo(
    () => buildMenuViewStateKey(currentBranch?.id),
    [currentBranch?.id]
  );
  const menuDraftStorageKey = useMemo(
    () => buildMenuDraftKey(currentBranch?.id),
    [currentBranch?.id]
  );
  const openDailyMenuEditor = useCallback(() => {
    navigate("/app/promos?tab=daily_menus");
  }, [navigate]);

  useEffect(() => {
    let active = true;

    const applyLocalStock = () => {
      if (!active) {
        return;
      }

      setIngredientStockMap(getIngredientStock(activeStockScope));
    };

    if (!activeStockScopeId || !isStockFeatureEnabled) {
      setIngredientStockMap({});
      return undefined;
    }

    const loadStock = async () => {
      try {
        const remoteStock = await hydrateIngredientStockFromRemote(activeStockScope);

        if (active) {
          setIngredientStockMap(remoteStock || {});
        }
      } catch {
        applyLocalStock();
      }
    };

    const handleStockUpdated = (event) => {
      const updatedRestaurantId = String(
        event?.detail?.restaurantId || "default"
      );
      const currentRestaurantId = String(activeStockScope?.id || "default");

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      if (active) {
        setIngredientStockMap(event?.detail?.stockMap || {});
      }
    };

    applyLocalStock();
    void loadStock();
    window.addEventListener(INGREDIENT_STOCK_UPDATED_EVENT, handleStockUpdated);

    return () => {
      active = false;
      window.removeEventListener(
        INGREDIENT_STOCK_UPDATED_EVENT,
        handleStockUpdated
      );
    };
  }, [activeStockScope, activeStockScopeId, isStockFeatureEnabled]);

  const buildLocalSnapshotPayload = useCallback(
    ({
      nextMenuId = menuId || "local-menu",
      nextCategorias = categorias,
      nextSubcategorias = subcategoriasPorCategoria,
      nextLinkedRules = linkedSubcategoryRules,
      nextPlatos = platos,
    } = {}) => ({
      menuId: nextMenuId,
      mode: menuMode,
      categories: nextCategorias,
      subcategoriasPorCategoria: nextSubcategorias,
      linkedSubcategoryRules: nextLinkedRules,
      products: nextPlatos,
    }),
    [categorias, linkedSubcategoryRules, menuId, menuMode, platos, subcategoriasPorCategoria]
  );

  const applySnapshot = useCallback((snapshot) => {
    setMenuId(snapshot?.menuId || "");
    setMenuMode(snapshot?.mode || "shared");
    setCategorias(snapshot?.categories || []);
    setSubcategoriasPorCategoria(snapshot?.subcategoriasPorCategoria || {});
    setLinkedSubcategoryRules(snapshot?.linkedSubcategoryRules || []);
    setPlatos((snapshot?.products || []).map((product) => ({ ...product, imageScope })));
  }, [imageScope]);

  const persistLocalSnapshot = useCallback(
    (nextSnapshot) => {
      saveLocalMenuSnapshot(currentBranch?.id, isSupabaseConfigured && !isCreatorMode
        ? toLightMenuSnapshot(nextSnapshot) : nextSnapshot);
    },
    [currentBranch?.id, isCreatorMode]
  );

  const productStockStateById = useMemo(() => {
    const nextState = new Map();

    platos.forEach((plato) => {
      const productId = normalizeEntityId(plato?.id);

      if (!productId) {
        return;
      }

      nextState.set(
        productId,
        getOperationalProductStockState(activeStockScope, plato, {
          ingredientes,
          stockMap: ingredientStockMap,
          stockEnabled: isStockFeatureEnabled,
          fallbackMessage:
            "Este plato quedo bloqueado hasta que repongas o cargues stock.",
        })
      );
    });

    return nextState;
  }, [
    activeStockScope,
    ingredientStockMap,
    ingredientes,
    isStockFeatureEnabled,
    platos,
  ]);

  const cargarMenu = useCallback(async () => {
    const request = ++menuRequestRef.current;
    if (!currentBranch?.id || isCreatorMode || !isSupabaseConfigured) {
      const localSnapshot = loadLocalMenuSnapshot(currentBranch?.id);
      applySnapshot(localSnapshot);
      setStorageMode("local");
      setSyncError("");
      setLoadingMenu(false);
      setMenuVerified(true);
      return localSnapshot;
    }

    const cached = loadLocalMenuSnapshot(currentBranch.id);
    const localSnapshot = cached?.menuId && !cached.menuId.startsWith("local-") ? toLightMenuSnapshot(cached) : null;
    const hasWarmSnapshot =
      (localSnapshot?.categories?.length || 0) > 0 ||
      (localSnapshot?.products?.length || 0) > 0;

    if (hasWarmSnapshot) {
      applySnapshot(localSnapshot);
    } else {
      applySnapshot(null);
    }

    setLoadingMenu(true);
    setStorageMode("supabase");
    setSyncError("");
    setMenuVerified(false);

    try {
      const snapshot = await getBranchMenuSnapshot(currentBranch.id, {
        includeInactiveProducts: true,
        includeImages: false,
      });
      if (request !== menuRequestRef.current) return null;
      const shouldUseLocalLinkedRules = snapshot?.linkedRulesUnavailable === true;
      const localLinkedRulesSnapshot = shouldUseLocalLinkedRules
        ? localSnapshot || loadLocalMenuSnapshot(currentBranch.id)
        : null;
      const nextSnapshot = {
        ...snapshot,
        linkedSubcategoryRules: shouldUseLocalLinkedRules
          ? mergeLinkedRules([], localLinkedRulesSnapshot?.linkedSubcategoryRules || [])
          : snapshot?.linkedSubcategoryRules || [],
      };
      applySnapshot(nextSnapshot);
      persistLocalSnapshot(nextSnapshot);
      setStorageMode("supabase");
      setSyncError("");
      setMenuVerified(true);
      return nextSnapshot;
    } catch (error) {
      if (request !== menuRequestRef.current) return null;
      // A connection failure must not discard a same-branch snapshot.
      if (!hasWarmSnapshot || /permission|jwt|unauthorized|forbidden/i.test(error.message || "")) applySnapshot(null);
      setStorageMode("supabase");
      setSyncError(error.message || "No se pudo cargar el menu.");
      return null;
    } finally {
      if (request === menuRequestRef.current) setLoadingMenu(false);
    }
  }, [applySnapshot, currentBranch?.id, isCreatorMode, persistLocalSnapshot]);

  useEffect(() => {
    cargarMenu();
    return () => { menuRequestRef.current += 1; };
  }, [cargarMenu]);

  useEffect(() => {
    setPendingDeliveryAvailability({});
  }, [currentBranch?.id]);

  useEffect(() => {
    const savedViewState = readSessionUiState(menuViewStateKey, null);

    setCategoriaActivaId(savedViewState?.categoriaActivaId || null);
    setModalCrearPlato(Boolean(savedViewState?.modalCrearPlato));
    setCategoriaInicialModal(savedViewState?.categoriaInicialModal || "");
    setPlatoEnEdicion(savedViewState?.platoEnEdicion || null);
    menuViewStateHydratedRef.current = menuViewStateKey;
  }, [menuViewStateKey]);

  useEffect(() => {
    if (menuViewStateHydratedRef.current !== menuViewStateKey) {
      return;
    }

    writeSessionUiState(menuViewStateKey, {
      categoriaActivaId,
      modalCrearPlato,
      categoriaInicialModal,
      platoEnEdicion,
    });
  }, [
    categoriaActivaId,
    categoriaInicialModal,
    menuViewStateKey,
    modalCrearPlato,
    platoEnEdicion,
  ]);

  const ensureReadyMenuId = useCallback(async () => {
    const safeMenuId = String(menuId || "").trim();

    if (safeMenuId) {
      return safeMenuId;
    }

    const snapshot = await cargarMenu();
    const resolvedMenuId = String(snapshot?.menuId || "").trim();

    if (!resolvedMenuId) {
      throw new Error(
        "Esta sucursal todavia no tiene un menu activo asignado. Primero asignalo o crealo desde Configuracion."
      );
    }

    return resolvedMenuId;
  }, [cargarMenu, menuId]);

  const categoriasConPlatos = useMemo(
    () =>
      categorias.map((categoria) => ({
        ...categoria,
        platos: platos.filter((plato) => plato.categoria === categoria.id),
      })),
    [categorias, platos]
  );

  const categoriaActiva =
    categoriasConPlatos.find((categoria) => categoria.id === categoriaActivaId) || null;

  useEffect(() => {
    setLinkedRulesExpanded(false);
  }, [categoriaActivaId, currentBranch?.id]);

  const pendingDeliveryChanges = useMemo(
    () =>
      Object.entries(pendingDeliveryAvailability).map(([productId, active]) => ({
        productId,
        active,
      })),
    [pendingDeliveryAvailability]
  );

  const allSubcategories = useMemo(
    () =>
      categorias.flatMap((categoria) =>
        (subcategoriasPorCategoria[categoria.id] || []).map((subcategoria) => ({
          ...subcategoria,
          categoriaId: categoria.id,
          categoriaNombre: categoria.nombre,
        }))
      ),
    [categorias, subcategoriasPorCategoria]
  );

  const activeCategorySubcategories = useMemo(
    () => (categoriaActiva ? subcategoriasPorCategoria[categoriaActiva.id] || [] : []),
    [categoriaActiva, subcategoriasPorCategoria]
  );

  const activeCategoryLinkedRules = useMemo(
    () =>
      linkedSubcategoryRules.filter((rule) => rule.sourceCategoryId === categoriaActiva?.id ||
        activeCategorySubcategories.some(
          (subcategoria) => subcategoria.id === rule.sourceSubcategoryId
        )
      ),
    [activeCategorySubcategories, categoriaActiva?.id, linkedSubcategoryRules]
  );

  const targetSubcategoryOptions = useMemo(
    () =>
      allSubcategories.filter(
        (subcategoria) =>
          subcategoria.categoriaId === targetCategoryId
      ),
    [allSubcategories, targetCategoryId]
  );

  const sourceChoices = useLinkedProductChoices({
    menuId, categoryId: categoriaActiva?.id, subcategoryId: sourceSubcategoryId,
    products: platos, remote: storageMode === "supabase" && !isCreatorMode,
  });
  const targetChoices = useLinkedProductChoices({
    menuId, categoryId: targetCategoryId, subcategoryId: targetSubcategoryId,
    products: platos, remote: storageMode === "supabase" && !isCreatorMode,
  });
  const sourceProductOptions = sourceChoices.products;
  const targetProductOptions = targetChoices.products.filter((plato) => plato.id !== sourceProductId);
  const relationSourceProduct = sourceProductOptions.find((item) => item.id === sourceProductId);
  const sourceIsBeverage = isBeverageCategory(categoriaActiva?.nombre) ||
    isBeverageCategory(allSubcategories.find((item) => item.id === sourceSubcategoryId)?.nombre) ||
    isBeverageCategory(allSubcategories.find((item) => item.id ===
      (relationSourceProduct?.subcategoriaId || relationSourceProduct?.subcategoryId))?.nombre) ||
    isBeverageCategory(relationSourceProduct?.subcategoria);
  const ingredientRelation = linkTargetType === "ingredient" && sourceIsBeverage;
  const ingredientOptions = ingredientes.filter((item) => item.active !== false);
  const relationTargetReady = ingredientRelation
    ? Boolean(sourceProductId && targetIngredientId && Number.isFinite(Number(consumptionMl)) && Number(consumptionMl) > 0)
    : Boolean(targetCategoryId && !targetChoices.loading && !targetChoices.error);

  useEffect(() => {
    setSourceSubcategoryId("");
    setSourceProductId("");
    setLinkTargetType("product");
    setTargetIngredientId("");
    setConsumptionMl("");
    setTargetCategoryId("");
    setTargetSubcategoryId("");
    setTargetProductId("");
    setLinkTitle("");
  }, [categoriaActiva?.id]);

  useEffect(() => {
    if (
      targetSubcategoryId &&
      !targetSubcategoryOptions.some(
        (subcategoria) => subcategoria.id === targetSubcategoryId
      )
    ) {
      setTargetSubcategoryId("");
    }
  }, [targetSubcategoryId, targetSubcategoryOptions]);

  const connectionMessage = useMemo(() => {
    if (storageMode === "supabase") {
      if (loadingMenu) return "Actualizando carta. Puedes recorrerla mientras comprobamos los datos actuales.";
      if (syncError) {
        return syncError;
      }

      const modeLabel =
        menuMode === "custom"
          ? "Esta sucursal usa un menu propio."
          : "Esta sucursal usa el menu compartido de la empresa.";

      return currentBranch?.name
        ? `Menu conectado a Supabase para la sucursal ${currentBranch.name}. ${modeLabel}`
        : `Menu conectado a Supabase. ${modeLabel}`;
    }

    if (isCreatorMode) {
      return "Modo creador activo. El menu queda guardado localmente en este navegador.";
    }

    return "Modo local activo para seguir probando el menu.";
  }, [currentBranch?.name, isCreatorMode, loadingMenu, menuMode, storageMode, syncError]);

  const notifyMenuPermissionDenied = useCallback(
    (actionLabel) => {
      setSyncError(
        `${currentAccessRoleLabel} puede revisar la carta, pero no ${actionLabel} en esta sucursal.`
      );
    },
    [currentAccessRoleLabel]
  );

  const abrirCrearPlato = (categoriaId = "") => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("abrir el editor de platos");
      return;
    }

    removeSessionUiState(menuDraftStorageKey);
    setPlatoEnEdicion(null);
    setCategoriaInicialModal(categoriaId);
    setModalCrearPlato(true);
  };

  const abrirEditarPlato = (plato) => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("editar platos");
      return;
    }

    removeSessionUiState(menuDraftStorageKey);
    setPlatoEnEdicion(plato);
    setCategoriaInicialModal(plato.categoria || "");
    setModalCrearPlato(true);
  };

  useEffect(() => {
    if (!getInternalAssistantEnvironment() || !canManageMenu || loadingMenu) return;
    const params = new URLSearchParams(location.search);
    const productId = params.get('setupProduct');
    if (!productId) return;
    const product = params.get('setupBranch') === (currentBranch?.id || currentRestaurant?.id) &&
      platos.find((item) => item.id === productId);
    params.delete('setupProduct'); params.delete('setupBranch');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    if (!product) return;
    removeSessionUiState(menuDraftStorageKey);
    setPlatoEnEdicion(product);
    setCategoriaInicialModal(product.categoria || '');
    setModalCrearPlato(true);
  }, [location.search, location.pathname, navigate, canManageMenu, loadingMenu, platos,
    currentBranch?.id, currentRestaurant?.id, menuDraftStorageKey]);

  const cerrarModal = () => {
    setModalCrearPlato(false);
    setPlatoEnEdicion(null);
    setCategoriaInicialModal("");
    removeSessionUiState(menuDraftStorageKey);
  };

  const agregarCategoria = async (nombreCategoria) => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("crear categorias");
      return null;
    }

    const nombre = nombreCategoria.trim();
    if (!nombre) {
      return null;
    }

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        const nuevaCategoria = await createMenuCategory(resolvedMenuId, {
          name: nombre,
          icon: buildCategoryIcon(nombre),
        });
        const nextCategorias = categorias.some((categoria) => categoria.id === nuevaCategoria.id)
          ? categorias.map((categoria) =>
              categoria.id === nuevaCategoria.id ? nuevaCategoria : categoria
            )
          : [...categorias, nuevaCategoria];
        const nextSubcategorias = {
          ...subcategoriasPorCategoria,
          [nuevaCategoria.id]: [],
        };
        setMenuId(resolvedMenuId);
        setCategorias(nextCategorias);
        setSubcategoriasPorCategoria(nextSubcategorias);
        persistLocalSnapshot(
          buildLocalSnapshotPayload({
            nextMenuId: resolvedMenuId,
            nextCategorias,
            nextSubcategorias,
          })
        );
        return nuevaCategoria;
      } catch (error) {
        setSyncError(error.message || "No se pudo crear la categoria.");
        return null;
      } finally {
        setSavingMenu(false);
      }
    }

    const existente = categorias.find(
      (categoria) => categoria.nombre.toLowerCase() === nombre.toLowerCase()
    );

    if (existente) {
      return existente;
    }

    const nuevaCategoria = {
      id: createLocalId("categoria"),
      nombre,
      icon: buildCategoryIcon(nombre),
    };

    const nextCategorias = [...categorias, nuevaCategoria];
    const nextSubcategorias = {
      ...subcategoriasPorCategoria,
      [nuevaCategoria.id]: [],
    };

    setCategorias(nextCategorias);
    setSubcategoriasPorCategoria(nextSubcategorias);
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextCategorias,
        nextSubcategorias,
      })
    );

    return nuevaCategoria;
  };

  const eliminarCategoria = async (categoriaId) => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("eliminar categorias");
      return false;
    }

    if (!categoriaId) {
      return false;
    }

    const tienePlatos = platos.some((plato) => plato.categoria === categoriaId);
    if (tienePlatos) {
      return false;
    }

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        await deleteMenuCategory(resolvedMenuId, categoriaId);
        await cargarMenu();
        setCategoriaActivaId((prev) => (prev === categoriaId ? null : prev));
        return true;
      } catch (error) {
        setSyncError(error.message || "No se pudo eliminar la categoria.");
        return false;
      } finally {
        setSavingMenu(false);
      }
    }

    const nextCategorias = categorias.filter((categoria) => categoria.id !== categoriaId);
    const nextSubcategorias = { ...subcategoriasPorCategoria };
    const categorySubcategoryIds = new Set(
      (subcategoriasPorCategoria[categoriaId] || []).map((subcategoria) => subcategoria.id)
    );
    const nextLinkedRules = linkedSubcategoryRules.filter(
      (rule) =>
        rule.sourceCategoryId !== categoriaId && rule.targetCategoryId !== categoriaId &&
        !categorySubcategoryIds.has(rule.sourceSubcategoryId) &&
        !categorySubcategoryIds.has(rule.targetSubcategoryId)
    );
    delete nextSubcategorias[categoriaId];

    setCategorias(nextCategorias);
    setSubcategoriasPorCategoria(nextSubcategorias);
    setLinkedSubcategoryRules(nextLinkedRules);
    setCategoriaActivaId((prev) => (prev === categoriaId ? null : prev));
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextCategorias,
        nextSubcategorias,
        nextLinkedRules,
      })
    );
    return true;
  };

  const agregarSubcategoria = async (categoriaId, nombreSubcategoria) => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("crear subcategorias");
      return false;
    }

    const nombre = nombreSubcategoria.trim();
    if (!categoriaId || !nombre) {
      return false;
    }

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        const subcategoria = await createMenuSubcategory(
          resolvedMenuId,
          categoriaId,
          nombre
        );
        const nextSubcategorias = {
          ...subcategoriasPorCategoria,
          [categoriaId]: (subcategoriasPorCategoria[categoriaId] || []).some(
            (currentSubcategory) => currentSubcategory.id === subcategoria.id
          )
            ? (subcategoriasPorCategoria[categoriaId] || []).map((currentSubcategory) =>
                currentSubcategory.id === subcategoria.id
                  ? subcategoria
                  : currentSubcategory
              )
            : [...(subcategoriasPorCategoria[categoriaId] || []), subcategoria],
        };
        setMenuId(resolvedMenuId);
        setSubcategoriasPorCategoria(nextSubcategorias);
        persistLocalSnapshot(
          buildLocalSnapshotPayload({
            nextMenuId: resolvedMenuId,
            nextSubcategorias,
          })
        );
        return subcategoria;
      } catch (error) {
        setSyncError(error.message || "No se pudo crear la subcategoria.");
        return false;
      } finally {
        setSavingMenu(false);
      }
    }

    const actuales = subcategoriasPorCategoria[categoriaId] || [];
    const existente = actuales.find(
      (subcategoria) => subcategoria.nombre.toLowerCase() === nombre.toLowerCase()
    );

    if (existente) {
      return existente;
    }

    const nuevaSubcategoria = {
      id: createLocalId("subcategoria"),
      nombre,
    };

    const nextSubcategorias = {
      ...subcategoriasPorCategoria,
      [categoriaId]: [...actuales, nuevaSubcategoria],
    };

    setSubcategoriasPorCategoria(nextSubcategorias);
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextSubcategorias,
      })
    );

    return nuevaSubcategoria;
  };

  const eliminarSubcategoria = async (categoriaId, subcategoriaId) => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("eliminar subcategorias");
      return false;
    }

    if (!categoriaId || !subcategoriaId) {
      return false;
    }

    const tienePlatos = platos.some(
      (plato) =>
        plato.categoria === categoriaId &&
        (plato.subcategoriaId === subcategoriaId ||
          plato.subcategoria?.toLowerCase() ===
            (subcategoriasPorCategoria[categoriaId] || [])
              .find((subcategoria) => subcategoria.id === subcategoriaId)
              ?.nombre?.toLowerCase())
    );

    if (tienePlatos) {
      return false;
    }

    const isLinked = linkedSubcategoryRules.some(
      (rule) =>
        rule.sourceSubcategoryId === subcategoriaId ||
        rule.targetSubcategoryId === subcategoriaId
    );

    if (isLinked) {
      window.alert(
        "No se puede eliminar esta subcategoria porque esta asociada a otra opcion del menu."
      );
      return false;
    }

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        await deleteMenuSubcategory(resolvedMenuId, categoriaId, subcategoriaId);
        await cargarMenu();
        return true;
      } catch (error) {
        setSyncError(error.message || "No se pudo eliminar la subcategoria.");
        return false;
      } finally {
        setSavingMenu(false);
      }
    }

    const nextSubcategorias = {
      ...subcategoriasPorCategoria,
      [categoriaId]: (subcategoriasPorCategoria[categoriaId] || []).filter(
        (subcategoria) => subcategoria.id !== subcategoriaId
      ),
    };

    setSubcategoriasPorCategoria(nextSubcategorias);
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextSubcategorias,
      })
    );

    return true;
  };

  const guardarPlato = async (plato) => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("guardar platos");
      return false;
    }

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        await saveMenuProduct(resolvedMenuId, plato);
        await cargarMenu();
        setCategoriaActivaId(plato.categoria || null);
        cerrarModal();
        return true;
      } catch (error) {
        setSyncError(error.message || "No se pudo guardar el plato.");
        throw error;
      } finally {
        setSavingMenu(false);
      }
    }

    const fotoUrl =
      plato.foto instanceof File
        ? await readFileAsDataUrl(plato.foto)
        : plato.fotoUrl || null;

    const platoNormalizado = {
      ...plato,
      id: plato.id || createLocalId("plato"),
      active: plato.active !== false,
      foto: null,
      fotoUrl,
    };

    const nextPlatos = plato.id
      ? platos.map((item) => (item.id === plato.id ? platoNormalizado : item))
      : [...platos, platoNormalizado];

    setPlatos(nextPlatos);
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextPlatos,
      })
    );
    setCategoriaActivaId(plato.categoria || null);
    cerrarModal();
    return true;
  };

  const eliminarPlato = async (platoId) => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("eliminar platos");
      return;
    }

    const confirmar = window.confirm("Queres eliminar este plato?");
    if (!confirmar) {
      return;
    }

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        await deleteMenuProduct(resolvedMenuId, platoId);
        await cargarMenu();
        setPendingDeliveryAvailability((currentChanges) => {
          if (!hasOwnEntry(currentChanges, platoId)) {
            return currentChanges;
          }

          const nextChanges = { ...currentChanges };
          delete nextChanges[platoId];
          return nextChanges;
        });
      } catch (error) {
        setSyncError(error.message || "No se pudo eliminar el plato.");
      } finally {
        setSavingMenu(false);
      }

      return;
    }

    const nextPlatos = platos.filter((plato) => plato.id !== platoId);
    const nextLinkedRules = linkedSubcategoryRules.filter((rule) =>
      rule.sourceProductId !== platoId && rule.targetProductId !== platoId
    );
    setLinkedSubcategoryRules(nextLinkedRules);
    setPlatos(nextPlatos);
    setPendingDeliveryAvailability((currentChanges) => {
      if (!hasOwnEntry(currentChanges, platoId)) {
        return currentChanges;
      }

      const nextChanges = { ...currentChanges };
      delete nextChanges[platoId];
      return nextChanges;
    });
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextPlatos,
        nextLinkedRules,
      })
    );
  };

  const actualizarPresentacionPlato = async (plato, changes) => {
    if (!canManageMenu || !plato?.id) {
      if (!canManageMenu) {
        notifyMenuPermissionDenied("cambiar la disponibilidad del menu");
      }
      return;
    }

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        await updateMenuProductPresentation(resolvedMenuId, plato.id, changes);
        await cargarMenu();
        setCategoriaActivaId(plato.categoria || null);
      } catch (error) {
        setSyncError(
          error.message ||
            "No se pudo actualizar la disponibilidad del plato."
        );
      } finally {
        setSavingMenu(false);
      }

      return;
    }

    const nextPlatos = platos.map((item) =>
      item.id === plato.id ? { ...item, ...changes } : item
    );
    setPlatos(nextPlatos);
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextPlatos,
      })
    );
  };

  const toggleDisponibilidadDelivery = (plato) => {
    if (!canManageDeliveryAvailability || !plato?.id) {
      if (!canManageDeliveryAvailability) {
        notifyMenuPermissionDenied(
          "cambiar la disponibilidad de los platos para Delivery"
        );
      }
      return;
    }

    const productId = normalizeEntityId(plato.id);
    const originalAvailability = plato.deliveryAvailable !== false;

    setPendingDeliveryAvailability((currentChanges) => {
      const currentAvailability = hasOwnEntry(currentChanges, productId)
        ? currentChanges[productId]
        : originalAvailability;
      const nextAvailability = !currentAvailability;
      const nextChanges = { ...currentChanges };

      if (nextAvailability === originalAvailability) {
        delete nextChanges[productId];
      } else {
        nextChanges[productId] = nextAvailability;
      }

      return nextChanges;
    });
  };

  const guardarCambiosDelivery = async () => {
    if (!canManageDeliveryAvailability) {
      notifyMenuPermissionDenied(
        "cambiar la disponibilidad de los platos para Delivery"
      );
      return;
    }

    if (pendingDeliveryChanges.length === 0) {
      return;
    }

    const changesByProductId = new Map(
      pendingDeliveryChanges.map((change) => [change.productId, change.active])
    );
    const nextPlatos = platos.map((item) =>
      changesByProductId.has(normalizeEntityId(item.id))
        ? {
            ...item,
            deliveryAvailable: changesByProductId.get(normalizeEntityId(item.id)),
          }
        : item
    );

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        await saveMenuProductsDeliveryAvailability({
          branchId: currentBranch?.id,
          changes: pendingDeliveryChanges,
        });
        setPlatos(nextPlatos);
        persistLocalSnapshot(
          buildLocalSnapshotPayload({
            nextPlatos,
          })
        );
        setPendingDeliveryAvailability({});
        setSyncError("");
      } catch (error) {
        setSyncError(
          error.message ||
            "No se pudo guardar la disponibilidad de los platos para Delivery."
        );
      } finally {
        setSavingMenu(false);
      }

      return;
    }

    setPlatos(nextPlatos);
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextPlatos,
      })
    );
    setPendingDeliveryAvailability({});
    setSyncError("");
  };

  const guardarLinkedSubcategoryRule = async () => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("guardar asociaciones guiadas");
      return;
    }

    if (savingMenu || sourceChoices.loading || sourceChoices.error || !categoriaActiva?.id || !relationTargetReady) {
      return;
    }

    const nextTitle = linkTitle.trim();
    const sourceSubcategory = allSubcategories.find(
      (subcategoria) => subcategoria.id === sourceSubcategoryId
    );
    const targetCategory = categorias.find(
      (categoria) => categoria.id === targetCategoryId
    );
    const targetSubcategory = allSubcategories.find(
      (subcategoria) => subcategoria.id === targetSubcategoryId
    );

    const sourceProduct = sourceProductOptions.find((plato) => plato.id === sourceProductId);
    const targetProduct = targetProductOptions.find((plato) => plato.id === targetProductId);
    const targetIngredient = ingredientOptions.find((item) => item.id === targetIngredientId);
    if ((ingredientRelation ? !targetIngredient : !targetCategory) || (sourceSubcategoryId && !sourceSubcategory) ||
        (sourceProductId && !sourceProduct) || (!ingredientRelation && targetProductId && !targetProduct)) {
      setSyncError("Revisa los platos seleccionados antes de guardar la asociacion.");
      return;
    }

    if (!ingredientRelation && ((!sourceProductId && !targetProductId && categoriaActiva.id === targetCategoryId &&
        (!sourceSubcategoryId || !targetSubcategoryId || sourceSubcategoryId === targetSubcategoryId)) ||
        (sourceProductId && sourceProductId === targetProductId))) {
      setSyncError("El origen y el destino no pueden ser el mismo grupo o plato.");
      return;
    }

    const existingRule = linkedSubcategoryRules.find(
      (rule) =>
        buildLinkedRuleKey(rule) ===
        buildLinkedRuleKey({
          sourceCategoryId: categoriaActiva.id,
          sourceSubcategoryId,
          sourceProductId,
          targetCategoryId,
          targetSubcategoryId,
          targetProductId,
          targetIngredientId: ingredientRelation ? targetIngredientId : "",
        })
    );
    const nextRule = {
      id: existingRule?.id || createLocalId("subcat-link"),
      sourceCategoryId: categoriaActiva.id,
      sourceCategoryName: categoriaActiva.nombre,
      sourceSubcategoryId,
      sourceSubcategoryName: sourceSubcategory?.nombre || "",
      sourceProductId,
      sourceProductName: sourceProduct?.nombre || "",
      targetCategoryId: ingredientRelation ? "" : targetCategoryId,
      targetCategoryName: ingredientRelation ? "" : targetCategory.nombre,
      targetSubcategoryId: ingredientRelation ? "" : targetSubcategoryId,
      targetSubcategoryName: ingredientRelation ? "" : targetSubcategory?.nombre || "",
      targetProductId: ingredientRelation ? "" : targetProductId,
      targetProductName: ingredientRelation ? "" : targetProduct?.nombre || "",
      targetIngredientId: ingredientRelation ? targetIngredientId : "",
      targetIngredientName: ingredientRelation ? targetIngredient.nombre || targetIngredient.name : "",
      consumptionMl: ingredientRelation ? Number(consumptionMl) : null,
      title: nextTitle,
      selectionMode: "single_optional",
      active: true,
    };
    const nextLinkedRules = mergeLinkedRules(
      linkedSubcategoryRules.filter(
        (rule) => buildLinkedRuleKey(rule) !== buildLinkedRuleKey(nextRule)
      ),
      [nextRule]
    );

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        await saveMenuSubcategoryLink(resolvedMenuId, {
          ...nextRule,
          id: existingRule?.id || "",
        });
        setMenuId(resolvedMenuId);
        setLinkedSubcategoryRules(nextLinkedRules);
        persistLocalSnapshot(
          buildLocalSnapshotPayload({
            nextMenuId: resolvedMenuId,
            nextLinkedRules,
          })
        );
        await cargarMenu();
      } catch (error) {
        setSyncError(error.message || "No se pudo guardar la asociacion.");
        return;
      } finally {
        setSavingMenu(false);
      }

      return;
    }

    setLinkedSubcategoryRules(nextLinkedRules);
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextLinkedRules,
      })
    );
    setTargetCategoryId("");
    setTargetSubcategoryId("");
    setTargetProductId("");
    setLinkTitle("");
  };

  const eliminarLinkedSubcategoryRule = async (rule) => {
    if (!canManageMenu) {
      notifyMenuPermissionDenied("eliminar asociaciones guiadas");
      return;
    }

    if (!rule?.id) {
      return;
    }

    if (storageMode === "supabase" && !isCreatorMode) {
      setSavingMenu(true);

      try {
        const resolvedMenuId = await ensureReadyMenuId();
        await deleteMenuSubcategoryLink(resolvedMenuId, rule.id);
        const nextLinkedRules = linkedSubcategoryRules.filter(
          (currentRule) => currentRule.id !== rule.id
        );
        setMenuId(resolvedMenuId);
        setLinkedSubcategoryRules(nextLinkedRules);
        persistLocalSnapshot(
          buildLocalSnapshotPayload({
            nextMenuId: resolvedMenuId,
            nextLinkedRules,
          })
        );
        await cargarMenu();
      } catch (error) {
        if (isMissingLinkedRulesTableError(error)) {
          const nextLinkedRules = linkedSubcategoryRules.filter(
            (currentRule) => currentRule.id !== rule.id
          );
          const resolvedMenuId = String(menuId || "").trim() || "local-menu";
          setMenuId(resolvedMenuId);
          setLinkedSubcategoryRules(nextLinkedRules);
          persistLocalSnapshot(
            buildLocalSnapshotPayload({
              nextMenuId: resolvedMenuId,
              nextLinkedRules,
            })
          );
          setSyncError(
            "La asociacion se elimino localmente en este navegador. Para sincronizarla tambien en Supabase falta correr menu_subcategory_links_patch.sql."
          );
          return;
        }

        setSyncError(error.message || "No se pudo eliminar la asociacion.");
        return;
      } finally {
        setSavingMenu(false);
      }

      return;
    }

    const nextLinkedRules = linkedSubcategoryRules.filter(
      (currentRule) => currentRule.id !== rule.id
    );

    setLinkedSubcategoryRules(nextLinkedRules);
    persistLocalSnapshot(
      buildLocalSnapshotPayload({
        nextLinkedRules,
      })
    );
  };

  const importPanel = getInternalAssistantEnvironment() && storageMode === "supabase" && menuId &&
    canSeeMenuModule && canPerformAction("manage_menu") ? (
      <MenuImportPanel key={`${imageScope}:${currentBranch?.id}:${menuId}`} menuId={menuId} userId={imageScope}
        companyName={currentRestaurant?.nombre || currentRestaurant?.name || "Restaurante"}
        branchName={currentBranch?.nombre || currentBranch?.name || "Sucursal actual"} shared={menuMode === "shared"}
        categories={categorias} subcategories={subcategoriasPorCategoria} products={platos}
        disabled={!canManageMenu || savingMenu} onImported={() => void cargarMenu()} />
    ) : null;

  const deliverySaveBar =
    canManageDeliveryAvailability && pendingDeliveryChanges.length > 0 ? (
      <div style={styles.deliverySaveBar}>
        <strong style={styles.deliverySaveStatus}>
          {pendingDeliveryChanges.length}{" "}
          {pendingDeliveryChanges.length === 1
            ? "cambio de delivery sin guardar"
            : "cambios de delivery sin guardar"}
        </strong>
        <div style={styles.deliverySaveActions}>
          <button
            type="button"
            style={styles.btnSecundario}
            onClick={() => setPendingDeliveryAvailability({})}
            disabled={savingMenu}
          >
            Descartar
          </button>
          <button
            type="button"
            style={styles.deliverySaveButton}
            onClick={guardarCambiosDelivery}
            disabled={savingMenu}
          >
            {savingMenu ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    ) : null;

  if (loadingMenu && categorias.length === 0 && platos.length === 0) {
    return (
      <div className="menu-admin" style={styles.container}>
        <div style={styles.loadingCard}>
          Estamos cargando el menu de la sucursal activa.
        </div>
      </div>
    );
  }

  if (categoriaActiva) {
    return (
      <div className="menu-admin" style={styles.container}>
        <div
          style={{
            ...styles.connectionBanner,
            ...(syncError ? styles.connectionBannerError : styles.connectionBannerInfo),
          }}
        >
          {savingMenu ? "Guardando cambios en el menu..." : connectionMessage}
          {syncError && !loadingMenu && <button type="button" onClick={cargarMenu} style={styles.btnSecundario}>Reintentar carga de carta</button>}
        </div>

        {!canManageMenu ? (
          <div style={styles.readOnlyNotice}>
            {menuReadOnly ? "Puedes recorrer la carta. La edicion se habilita al confirmar los datos actuales."
              : `${currentAccessRoleLabel} puede recorrer la carta y las combinaciones, pero no editar categorias, platos ni asociaciones en esta sucursal.`}
          </div>
        ) : null}

        {deliverySaveBar}
        {importPanel}

        <div style={styles.panelCategoriaSolo}>
          <div style={styles.panelHeader}>
            <div>
              <button
                type="button"
                style={styles.btnSecundario}
                onClick={() => setCategoriaActivaId(null)}
              >
                Volver a categorias
              </button>
              <h2 style={styles.panelTituloGrande}>
                {categoriaActiva.icon} {categoriaActiva.nombre}
              </h2>
            </div>

            {canManageMenu ? (
              <button
                style={styles.btnPrimario}
                onClick={() => abrirCrearPlato(categoriaActiva.id)}
              >
                + Nuevo plato
              </button>
            ) : (
              <span style={styles.readOnlyBadge}>Solo lectura</span>
            )}
          </div>

          <section style={styles.rulePanel}>
            <div style={styles.rulePanelHeader}>
              <div>
                <h3 style={styles.rulePanelTitle}>Combinaciones guiadas</h3>
                <p style={styles.rulePanelText}>
                  {activeCategoryLinkedRules.length}{" "}
                  {activeCategoryLinkedRules.length === 1
                    ? "asociacion configurada"
                    : "asociaciones configuradas"}
                </p>
              </div>
              <button
                type="button"
                aria-expanded={linkedRulesExpanded}
                aria-controls={linkedRulesPanelId}
                style={{ ...styles.btnSecundario, ...styles.ruleToggle }}
                onClick={() => setLinkedRulesExpanded((expanded) => !expanded)}
              >
                {linkedRulesExpanded ? "Ocultar combinaciones" : "Mostrar combinaciones"}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false"
                  style={{ transform: linkedRulesExpanded ? "rotate(180deg)" : "none" }}>
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            </div>

            <div id={linkedRulesPanelId} hidden={!linkedRulesExpanded}>
            {linkedRulesExpanded && (
              <>
                <p style={styles.rulePanelText}>
                  Elige el grupo o plato de origen y lo que quieres ofrecer como
                  opcion asociada. Puedes relacionar toda una categoria, una
                  subcategoria o un plato especifico. Solo los platos asociados
                  dejan de mostrarse solos en la carta QR.
                </p>
                <div style={styles.ruleFormGrid}>
                  <label style={styles.ruleField}>
                    <span style={styles.ruleLabel}>Subcategoria de origen</span>
                    <select
                      value={sourceSubcategoryId}
                      onChange={(event) => {
                        setSourceSubcategoryId(event.target.value);
                        setSourceProductId("");
                      }}
                      style={styles.ruleSelect}
                      disabled={!canManageMenu}
                    >
                      <option value="">Toda la categoria {categoriaActiva.nombre}</option>
                      {activeCategorySubcategories.map((subcategoria) => (
                        <option key={subcategoria.id} value={subcategoria.id}>
                          {subcategoria.nombre}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={styles.ruleField}>
                    <span style={styles.ruleLabel}>Plato de origen</span>
                    <select value={sourceProductId} style={styles.ruleSelect}
                      aria-label="Plato de origen" aria-describedby="source-product-status"
                      disabled={!canManageMenu || sourceChoices.loading || Boolean(sourceChoices.error)} onChange={(event) => {
                        setSourceProductId(event.target.value);
                        setTargetProductId("");
                      }}>
                      <option value="">{sourceChoices.loading ? "Cargando platos..." : "Todos los platos del grupo"}</option>
                      {sourceProductOptions.map((plato) => (
                        <option key={plato.id} value={plato.id}>{plato.nombre}</option>
                      ))}
                    </select>
                    <small id="source-product-status" role="status">
                      {sourceChoices.error || (sourceChoices.loading ? "Consultando los platos del menu..." :
                        sourceProductOptions.length ? `${sourceProductOptions.length} platos en este grupo` :
                          "No hay platos cargados en esta categoria y subcategoria.")}
                    </small>
                    {sourceChoices.error ? <button type="button" style={styles.btnSecundario}
                      onClick={sourceChoices.refresh}>Reintentar platos de origen</button> : null}
                  </label>

                  {sourceIsBeverage ? <label style={styles.ruleField}>
                    <span style={styles.ruleLabel}>Relacionar con</span>
                    <select aria-label="Relacionar con" style={styles.ruleSelect} value={linkTargetType}
                      disabled={!canManageMenu} onChange={(event) => setLinkTargetType(event.target.value)}>
                      <option value="product">Plato del menu</option>
                      <option value="ingredient">Ingrediente de bebida (ml)</option>
                    </select>
                  </label> : null}
                  {ingredientRelation ? <>
                    <label style={styles.ruleField}>
                      <span style={styles.ruleLabel}>Ingrediente de stock</span>
                      <select aria-label="Ingrediente de stock" style={styles.ruleSelect} value={targetIngredientId}
                        disabled={!canManageMenu} onChange={(event) => setTargetIngredientId(event.target.value)}>
                        <option value="">Elegi Pilsen, Honey u otra variedad</option>
                        {ingredientOptions.map((item) => <option key={item.id} value={item.id}>{item.nombre || item.name}</option>)}
                      </select>
                    </label>
                    <label style={styles.ruleField}>
                      <span style={styles.ruleLabel}>Consumo por vaso (ml)</span>
                      <input type="number" min="0.01" step="0.01" aria-label="Consumo por vaso (ml)"
                        style={styles.ruleInput} value={consumptionMl} placeholder="Ej: 350"
                        disabled={!canManageMenu} onChange={(event) => setConsumptionMl(event.target.value)} />
                    </label>
                    <p style={styles.rulePanelText}>
                      Elegi un plato de origen concreto (por ejemplo, Chopp 350 ml). El cliente elige una variedad
                      por vaso, sin adicional al precio del plato. El stock de ese ingrediente debe estar en ml o litros.
                      Si tambien esta en la receta, se reemplaza por esta eleccion para no descontarlo dos veces.
                      Para cambiar el consumo, selecciona la misma relacion y guardala de nuevo.
                    </p>
                  </> : <>
                  <label style={styles.ruleField}>
                    <span style={styles.ruleLabel}>Categoria asociada</span>
                    <select
                      value={targetCategoryId}
                      onChange={(event) => {
                        setTargetCategoryId(event.target.value);
                        setTargetSubcategoryId("");
                        setTargetProductId("");
                      }}
                      style={styles.ruleSelect}
                      disabled={!canManageMenu}
                    >
                      <option value="">Selecciona una categoria</option>
                      {categorias.map((categoria) => (
                        <option key={categoria.id} value={categoria.id}>
                          {categoria.nombre}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={styles.ruleField}>
                    <span style={styles.ruleLabel}>Subcategoria puntual</span>
                    <select
                      value={targetSubcategoryId}
                      onChange={(event) => {
                        setTargetSubcategoryId(event.target.value);
                        setTargetProductId("");
                      }}
                      style={styles.ruleSelect}
                      disabled={!canManageMenu || !targetCategoryId}
                    >
                      <option value="">Toda la categoria</option>
                      {targetSubcategoryOptions.map((subcategoria) => (
                        <option key={subcategoria.id} value={subcategoria.id}>
                          {subcategoria.nombre}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={styles.ruleField}>
                    <span style={styles.ruleLabel}>Plato asociado</span>
                    <select value={targetProductId} style={styles.ruleSelect}
                      aria-label="Plato asociado" aria-describedby="target-product-status"
                      disabled={!canManageMenu || !targetCategoryId || targetChoices.loading || Boolean(targetChoices.error)}
                      onChange={(event) => setTargetProductId(event.target.value)}>
                      <option value="">{targetChoices.loading ? "Cargando platos..." : "Todos los platos del grupo"}</option>
                      {targetProductOptions.map((plato) => (
                        <option key={plato.id} value={plato.id}>{plato.nombre}</option>
                      ))}
                    </select>
                    {targetCategoryId ? <small id="target-product-status" role="status">
                      {targetChoices.error || (targetChoices.loading ? "Consultando los platos del menu..." :
                        targetProductOptions.length ? `${targetProductOptions.length} platos en este grupo` :
                          "No hay otros platos cargados en esta categoria y subcategoria.")}
                    </small> : null}
                    {targetChoices.error ? <button type="button" style={styles.btnSecundario}
                      onClick={targetChoices.refresh}>Reintentar platos asociados</button> : null}
                  </label>
                  </>}

                  <div style={styles.ruleField}>
                    <span style={styles.ruleLabel}>Eleccion</span>
                    <div
                      style={{
                        ...styles.ruleInput,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#f8fafc",
                        color: "#475569",
                      }}
                    >
                      {ingredientRelation ? "Variedad obligatoria por vaso, sin preseleccion" : "Nada aparece seleccionada por defecto"}
                    </div>
                  </div>

                  <label style={styles.ruleField}>
                    <span style={styles.ruleLabel}>Titulo visible</span>
                    <input
                      type="text"
                      value={linkTitle}
                      onChange={(event) => setLinkTitle(event.target.value)}
                      placeholder="Ej: Elegi una salsa"
                      style={styles.ruleInput}
                      disabled={!canManageMenu}
                    />
                  </label>
                </div>

                {canManageMenu ? (
                  <div style={styles.ruleActions}>
                    <button
                      type="button"
                      style={styles.btnPrimario}
                      onClick={guardarLinkedSubcategoryRule}
                      disabled={!canManageMenu || savingMenu || !relationTargetReady || sourceChoices.loading || Boolean(sourceChoices.error)}
                    >
                      Guardar asociacion
                    </button>
                  </div>
                ) : null}
            <div style={styles.ruleList}>
              {activeCategoryLinkedRules.length === 0 ? (
                <div style={styles.emptyState}>
                  Todavia no hay asociaciones creadas para esta categoria.
                </div>
              ) : (
                activeCategoryLinkedRules.map((rule) => (
                  <article key={rule.id} style={styles.ruleCard}>
                    <div>
                      <strong style={styles.ruleCardTitle}>
                        {[rule.sourceCategoryName || categoriaActiva.nombre,
                          rule.sourceSubcategoryName,
                          rule.sourceProductName || "Todos los platos del grupo"].filter(Boolean).join(" / ")}
                        {" -> "}{buildLinkedRuleTargetLabel(rule)}
                      </strong>
                      <div style={styles.ruleMetaRow}>
                        <span style={styles.ruleMetaChip}>
                          {isIngredientLinkedRule(rule) ? "Variedad obligatoria" : "Incluye Nada"}
                        </span>
                        {rule.title ? (
                          <span style={styles.ruleMetaChip}>{rule.title}</span>
                        ) : null}
                        <span style={styles.ruleMetaChip}>
                          Solo dentro del plato principal
                        </span>
                      </div>
                    </div>

                    {canManageMenu ? (
                      <button
                        type="button"
                        style={styles.btnPeligro}
                        onClick={() => eliminarLinkedSubcategoryRule(rule)}
                      >
                        Eliminar
                      </button>
                    ) : null}
                  </article>
                ))
              )}
            </div>
              </>
            )}
            </div>
          </section>

          {categoriaActiva.platos.length === 0 ? (
            <div style={styles.emptyState}>
              Esta categoria todavia no tiene platos cargados.
            </div>
          ) : (
            <div className="menu-admin__products" style={styles.gridPlatos}>
              {categoriaActiva.platos.map((plato) => {
                const productStockState =
                  productStockStateById.get(normalizeEntityId(plato.id)) || {
                    available: true,
                    reason: "",
                  };
                const isBlockedByStock = productStockState.available === false;
                const isManuallyHidden = !isProductVisibleInMenu(plato);
                const isManuallyUnavailable = plato.active === false;
                const isOperationallyUnavailable =
                  isManuallyUnavailable || isBlockedByStock;
                const productId = normalizeEntityId(plato.id);
                const hasPendingDeliveryChange = hasOwnEntry(
                  pendingDeliveryAvailability,
                  productId
                );
                const isDeliveryAvailable = hasPendingDeliveryChange
                  ? pendingDeliveryAvailability[productId]
                  : plato.deliveryAvailable !== false;

                return (
                  <div
                    key={plato.id}
                    className="menu-admin__product"
                    style={{
                      ...styles.cardPlato,
                      ...(isOperationallyUnavailable ? styles.cardPlatoDisabled : {}),
                      ...(isBlockedByStock
                        ? {
                            borderColor: "#fecaca",
                            backgroundColor: "#fff7f7",
                          }
                        : {}),
                    }}
                  >
                  <MenuProductImage product={plato} enabled={!loadingMenu}
                    style={styles.imagenPlato} />

                  <div style={styles.cardBody}>
                    <strong style={styles.platoNombre}>{plato.nombre}</strong>
                    <div style={styles.precio}>
                      ${Number(plato.precio || 0).toFixed(2)}
                    </div>

                    {plato.descripcion && (
                      <p style={styles.descripcion}>{plato.descripcion}</p>
                    )}

                    <div style={styles.meta}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...(isManuallyUnavailable
                            ? styles.statusBadgeDisabled
                            : isBlockedByStock
                              ? {
                                  backgroundColor: "#fef2f2",
                                  color: "#b91c1c",
                                  border: "1px solid #fecaca",
                                }
                              : styles.statusBadgeAvailable),
                        }}
                      >
                        {isManuallyUnavailable
                          ? "No disponible"
                          : isBlockedByStock
                            ? "Sin stock"
                            : isManuallyHidden ? "Oculto en carta; disponible en relaciones" : "Disponible"}
                      </span>
                      <span>Subcategoria: {plato.subcategoria}</span>
                      <span>
                        Ingredientes: {contarIngredientesPlato(plato.ingredientes || [])}
                      </span>
                    </div>

                    {isBlockedByStock ? (
                      <p
                        style={{
                          ...styles.descripcion,
                          marginTop: 0,
                          color: "#b91c1c",
                        }}
                      >
                        {productStockState.reason ||
                          "Este plato quedo bloqueado por falta de stock."}
                      </p>
                    ) : null}

                    {(canManageMenu || canManageDeliveryAvailability) && (
                      <div className="menu-admin__product-actions" style={styles.actions}>
                        {canManageMenu ? (
                          <>
                          <label style={styles.availabilityToggle}>
                            <input
                              type="checkbox"
                              checked={!isManuallyHidden}
                              disabled={savingMenu}
                              onChange={() => actualizarPresentacionPlato(plato, { visibleInMenu: isManuallyHidden })}
                            />
                            <span>
                              Visible en carta
                            </span>
                          </label>
                          <label style={styles.availabilityToggle}>
                            <input
                              type="checkbox"
                              checked={!isManuallyUnavailable}
                              disabled={savingMenu}
                              onChange={() => actualizarPresentacionPlato(plato, { active: isManuallyUnavailable })}
                            />
                            <span>Disponible para pedir</span>
                          </label>
                          <small>Ocultarlo de la carta no lo quita de sus relaciones. Desactivar la disponibilidad lo bloquea en ambas.</small>
                          </>
                        ) : null}
                        {canManageDeliveryAvailability ? (
                          <label
                            style={{
                              ...styles.availabilityToggle,
                              ...(isDeliveryAvailable
                                ? styles.deliveryToggleAvailable
                                : styles.deliveryToggleDisabled),
                              ...(hasPendingDeliveryChange
                                ? styles.deliveryTogglePending
                                : {}),
                            }}
                            title="Controla si este plato aparece en el menu web y el chatbot de Delivery."
                          >
                            <input
                              type="checkbox"
                              checked={isDeliveryAvailable}
                              onChange={() => toggleDisponibilidadDelivery(plato)}
                              disabled={savingMenu}
                            />
                            <span>
                              {isDeliveryAvailable
                                ? "Disponible para delivery"
                                : "No disponible en delivery"}
                            </span>
                          </label>
                        ) : null}
                        {canManageMenu ? (
                          <>
                            <button
                              type="button"
                              style={styles.btnSecundario}
                              onClick={() => abrirEditarPlato(plato)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              style={styles.btnPeligro}
                              onClick={() => eliminarPlato(plato.id)}
                            >
                              Eliminar
                            </button>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {modalCrearPlato && canManageMenu && (
          <ModalCrearPlato
            categorias={categorias}
            subcategoriasPorCategoria={subcategoriasPorCategoria}
            platos={platos}
            categoriaInicial={categoriaInicialModal}
            initialData={platoEnEdicion}
            draftStorageKey={menuDraftStorageKey}
            onAgregarCategoria={agregarCategoria}
            onEliminarCategoria={eliminarCategoria}
            onAgregarSubcategoria={agregarSubcategoria}
            onEliminarSubcategoria={eliminarSubcategoria}
            onClose={cerrarModal}
            onGuardar={guardarPlato}
          />
        )}
      </div>
    );
  }

  return (
    <div className="menu-admin" style={styles.container}>
      <div
        style={{
          ...styles.connectionBanner,
          ...(syncError ? styles.connectionBannerError : styles.connectionBannerInfo),
        }}
      >
        {savingMenu ? "Guardando cambios en el menu..." : connectionMessage}
        {syncError && !loadingMenu && <button type="button" onClick={cargarMenu} style={styles.btnSecundario}>Reintentar carga de carta</button>}
      </div>

      {!canManageMenu ? (
        <div style={styles.readOnlyNotice}>
          {menuReadOnly ? "Puedes recorrer la carta. La edicion se habilita al confirmar los datos actuales."
            : `${currentAccessRoleLabel} puede consultar categorias y platos, pero no crear ni editar la carta en esta sucursal.`}
        </div>
      ) : null}

      {deliverySaveBar}
      {importPanel}

      <div style={styles.header}>
        <div style={styles.headerTitleWrap}>
          <h2 style={styles.titulo}>Categorias gastronomicas</h2>
        </div>

        {canManageMenu ? (
          <div style={styles.headerActions}>
            <button style={styles.btnSecundario} onClick={openDailyMenuEditor}>
              + Crear menu del dia
            </button>
            <button
              style={styles.btnPrimario}
              onClick={() => abrirCrearPlato(categoriaActivaId || "")}
            >
              + Crear plato
            </button>
          </div>
        ) : (
          <span style={styles.readOnlyBadge}>Acceso de solo lectura</span>
        )}
      </div>

      <div className="menu-admin__categories" style={styles.gridCategorias}>
        {categoriasConPlatos.map((categoria) => (
          <CategoriaBoton
            key={categoria.id}
            icon={categoria.icon}
            label={categoria.nombre}
            total={categoria.platos.length}
            active={categoria.id === categoriaActivaId}
            preview={categoria.platos.slice(0, 2)}
            imagesEnabled={!loadingMenu}
            onClick={() => setCategoriaActivaId(categoria.id)}
          />
        ))}
      </div>

      {modalCrearPlato && canManageMenu && (
        <ModalCrearPlato
          categorias={categorias}
          subcategoriasPorCategoria={subcategoriasPorCategoria}
          platos={platos}
          categoriaInicial={categoriaInicialModal}
          initialData={platoEnEdicion}
          draftStorageKey={menuDraftStorageKey}
          onAgregarCategoria={agregarCategoria}
          onEliminarCategoria={eliminarCategoria}
          onAgregarSubcategoria={agregarSubcategoria}
          onEliminarSubcategoria={eliminarSubcategoria}
          onClose={cerrarModal}
          onGuardar={guardarPlato}
        />
      )}
    </div>
  );
}

const styles = {
  container: { width: "100%", padding: "var(--menu-admin-padding, 20px)", boxSizing: "border-box" },
  loadingCard: {
    padding: 18,
    borderRadius: 12,
    backgroundColor: "#ffffff",
    border: "1px solid #dbe2ea",
    color: "#475569",
  },
  connectionBanner: {
    marginBottom: 18,
    padding: "12px 14px",
    borderRadius: 12,
    fontSize: 14,
    lineHeight: 1.45,
  },
  connectionBannerInfo: {
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
  },
  connectionBannerError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
  },
  readOnlyNotice: {
    marginBottom: 18,
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid #cbd5f5",
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontSize: 14,
    lineHeight: 1.5,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    position: "relative",
  },
  titulo: {
    margin: 0,
    textAlign: "center",
  },
  headerTitleWrap: {
    flex: "var(--menu-title-flex, 1)",
    display: "flex",
    justifyContent: "center",
    minWidth: "var(--menu-title-min-width, 240px)",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  btnPrimario: {
    backgroundColor: "#3498db",
    color: "#fff",
    padding: "10px 15px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
  },
  btnSecundario: {
    backgroundColor: "#fff",
    color: "#111827",
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    cursor: "pointer",
  },
  btnPeligro: {
    backgroundColor: "#fff",
    color: "#dc2626",
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #dc2626",
    cursor: "pointer",
  },
  readOnlyBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 12px",
    borderRadius: 999,
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 700,
    fontSize: 13,
  },
  gridCategorias: {
    display: "grid",
    gridTemplateColumns: "var(--menu-category-columns, repeat(auto-fit, minmax(180px, 210px)))",
    justifyContent: "center",
    gap: "var(--menu-grid-gap, 15px)",
    marginTop: 20,
    width: "100%",
  },
  btnCategoria: {
    padding: "var(--menu-category-padding, 15px)",
    background: "#f8f8f8",
    borderRadius: 12,
    cursor: "pointer",
    textAlign: "center",
    border: "1px solid #e5e7eb",
    width: "100%",
  },
  btnCategoriaActiva: {
    border: "1px solid #3498db",
    boxShadow: "0 0 0 1px rgba(52,152,219,0.2)",
    background: "#eef7ff",
  },
  emoji: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 8,
  },
  categoriaNombre: {
    fontWeight: 600,
  },
  categoriaMeta: {
    marginTop: 8,
    color: "#6b7280",
    fontSize: 14,
  },
  previewStrip: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 6,
    marginBottom: 10,
  },
  previewThumb: {
    width: "100%",
    height: "var(--menu-preview-height, 72px)",
    objectFit: "cover",
    borderRadius: 8,
    display: "block",
  },
  previewThumbPlaceholder: {
    height: 72,
    borderRadius: 8,
    backgroundColor: "#eef2f7",
    color: "#6b7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
  },
  panelCategoriaSolo: {
    marginTop: 8,
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  panelTituloGrande: {
    marginTop: 12,
    marginBottom: 0,
  },
  rulePanel: {
    marginTop: 18,
    padding: 16,
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
  },
  rulePanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },
  rulePanelTitle: {
    margin: 0,
    fontSize: 18,
    color: "#111827",
  },
  ruleToggle: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 44,
    maxWidth: "100%",
  },
  rulePanelText: {
    marginTop: 6,
    marginBottom: 0,
    color: "#6b7280",
    fontSize: 14,
    lineHeight: 1.45,
  },
  ruleFormGrid: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  ruleField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  ruleLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
  },
  ruleSelect: {
    minHeight: 40,
    borderRadius: 10,
    border: "1px solid #d1d5db",
    padding: "0 12px",
    backgroundColor: "#fff",
    color: "#111827",
  },
  ruleInput: {
    minHeight: 40,
    borderRadius: 10,
    border: "1px solid #d1d5db",
    padding: "0 12px",
    backgroundColor: "#fff",
    color: "#111827",
  },
  ruleActions: {
    marginTop: 14,
    display: "flex",
    justifyContent: "flex-end",
  },
  ruleList: {
    marginTop: 18,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  ruleCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    padding: 14,
    borderRadius: 12,
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
  },
  ruleCardTitle: {
    color: "#111827",
  },
  ruleMetaRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 8,
  },
  ruleMetaChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontSize: 12,
    fontWeight: 700,
  },
  emptyState: {
    marginTop: 20,
    padding: 18,
    borderRadius: 10,
    backgroundColor: "#f9fafb",
    border: "1px dashed #d1d5db",
    color: "#4b5563",
  },
  gridPlatos: {
    display: "grid",
    gridTemplateColumns: "var(--menu-product-columns, repeat(auto-fill, minmax(260px,1fr)))",
    gap: "var(--menu-grid-gap, 16px)",
    marginTop: 20,
  },
  cardPlato: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    background: "#fff",
  },
  cardPlatoDisabled: {
    opacity: 0.72,
    borderColor: "#fca5a5",
  },
  imagenPlato: {
    width: "100%",
    height: "var(--menu-product-photo-height, 180px)",
    objectFit: "cover",
    display: "block",
  },
  imagenPlaceholder: {
    height: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
  },
  cardBody: {
    padding: "var(--menu-product-padding, 14px)",
  },
  platoNombre: {
    display: "block",
  },
  precio: {
    marginTop: 8,
    fontWeight: 700,
    color: "#111827",
  },
  descripcion: {
    marginTop: 10,
    marginBottom: 10,
    color: "#4b5563",
  },
  meta: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    color: "#6b7280",
    fontSize: 14,
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
  },
  statusBadgeAvailable: {
    backgroundColor: "#ecfdf5",
    color: "#047857",
    border: "1px solid #a7f3d0",
  },
  statusBadgeDisabled: {
    backgroundColor: "#fff1f2",
    color: "#b91c1c",
    border: "1px solid #fecdd3",
  },
  actions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 14,
  },
  deliverySaveBar: {
    position: "fixed",
    left: "50%",
    bottom: 18,
    transform: "translateX(-50%)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    width: "min(620px, calc(100vw - 32px))",
    boxSizing: "border-box",
    padding: "12px 14px",
    border: "1px solid #86efac",
    borderRadius: 8,
    backgroundColor: "#f0fdf4",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
  },
  deliverySaveStatus: {
    color: "#166534",
    fontSize: 14,
  },
  deliverySaveActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  deliverySaveButton: {
    padding: "9px 14px",
    border: "none",
    borderRadius: 8,
    backgroundColor: "#15803d",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  availabilityToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    color: "#374151",
    backgroundColor: "#f9fafb",
    fontSize: 13,
    fontWeight: 600,
  },
  deliveryToggleAvailable: {
    borderColor: "#93c5fd",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
  },
  deliveryToggleDisabled: {
    borderColor: "#d1d5db",
    backgroundColor: "#f9fafb",
    color: "#6b7280",
  },
  deliveryTogglePending: {
    boxShadow: "0 0 0 2px rgba(22, 163, 74, 0.28)",
  },
};

export default Menu;
